import React from 'react'
import {
  Bullet,
  Bullets,
  DocLink,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../../components/CommandControlComponents'

const Overview = () => (
  <Page
    title="Control Room Overview"
    intro="The control room is the live command workspace for an event. It brings incidents, resources, maps, command logs, complex decisions, treatment centres, and operational metrics into one shared view."
  >
    <Section title="Where to open it">
      <Bullets>
        <Bullet>
          Open{' '}
          <DocLink
            url="/commandcontrol/controlroom"
            text="Command & Control > Control Room"
          />{' '}
          to view active, planned, or recently used rooms.
        </Bullet>
        <Bullet>
          Start a room from an event when the event needs live CAD-style
          oversight.
        </Bullet>
        <Bullet>
          Use the control room dashboard to reopen an existing room or review
          recent command activity.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Main capabilities">
      <Bullets>
        <Bullet>Three operator views: compact, Screen 1, and Screen 2.</Bullet>
        <Bullet>
          Live incident creation, call triage, dispatch, resource assignment,
          and incident detail editing.
        </Bullet>
        <Bullet>
          Live geographic map, uploaded event map, a scaled overlay view, and a
          GPX course view — see{' '}
          <DocLink
            docsPage
            url="/command-control/maps"
            text="Maps and Event Maps"
          />
          .
        </Bullet>
        <Bullet>
          Named, searchable map pins for fixed site features, and Commander-run
          GPX route uploads for course events.
        </Bullet>
        <Bullet>
          Resource status, crew assignment, manual event-map placement, and
          resource timing updates.
        </Bullet>
        <Bullet>
          Two-way crew messaging between the control room and each resource's
          crew phones — chat sent while a resource is on an incident also lands
          in that incident's log. See{' '}
          <DocLink
            docsPage
            url="/command-control/crew-messaging"
            text="Crew Messaging"
          />
          .
        </Bullet>
        <Bullet>
          Consent-based live location sharing from crew members' phones.
        </Bullet>
        <Bullet>
          Command logs, incident logs, complex decisions, and tactical-log
          transfer for audit trails.
        </Bullet>
        <Bullet>
          Treatment centre summaries, capacity, patient flow, and linked
          treatment-location configuration, plus web and mobile treatment
          workstations for shared bed boards — see{' '}
          <DocLink
            docsPage
            url="/command-control/treatment-centres"
            text="Treatment Centres"
          />
          .
        </Bullet>
        <Bullet>
          Control metrics with an all-days/day selector for multi-day events.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Live updates">
      <p>
        The control room uses a live snapshot refresh and versioning. Operators
        see active incidents, resource states, treatment locations, metrics, and
        map positions update without needing to reload the page. The header
        shows whether the room is live, delayed, or offline.
      </p>
      <Info>
        Expired breaks are not ended automatically. When a break timer runs out,
        the resource card pulses in the control room and a dispatcher ends the
        break from the resource drawer.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <p>
        Access is tiered by CAD access level: Viewers get a live read-only board
        with a View Only chip, Call Takers and Dispatchers can run operations,
        and Commanders control the room lifecycle and command decisions. See the
        Permissions and Audit page in this section for the full breakdown.
      </p>
    </Section>

    <Screenshot url="command-control/control-room-header.png">
      Control room header showing live sync status, the Compact / Screen 1 /
      Screen 2 view selector, and the map view selector (Map, Event, Overlay,
      and GPX).
    </Screenshot>
  </Page>
)

export default Overview
